import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, Close, Download, SaveAlt } from "@mui/icons-material";
import { isMobile, isWeChat, prefetch } from "./app/save";

export type ViewerImage = { key: string; name: string; url: string };

type T = { x: number; y: number; s: number };
const clamp = (v: number, a: number, b: number) => Math.min(b, Math.max(a, v));

/** 全屏看图：左右滑动切换、双指缩放、双击放大、单击显示/隐藏控件 */
export default function ImageViewer({ images, index, onIndex, onClose, onDownload }: {
  images: ViewerImage[]; index: number; onIndex: (i: number) => void; onClose: () => void; onDownload: (img: ViewerImage) => void;
}) {
  const [t, setT] = useState<T>({ x: 0, y: 0, s: 1 });
  const [dx, setDx] = useState(0); // 未缩放时的滑动偏移
  const [anim, setAnim] = useState(false);
  const [controls, setControls] = useState(true);
  const [loaded, setLoaded] = useState<Record<string, boolean>>({});
  const box = useRef<HTMLDivElement>(null);
  const g = useRef<any>({});
  const img = images[index];

  useEffect(() => { setT({ x: 0, y: 0, s: 1 }); setDx(0); }, [index]);
  // 手机上提前准备好当前图片，点“保存到相册”时可立即调起系统分享
  useEffect(() => { if (isMobile() && !isWeChat() && images[index]) prefetch(images[index].key).catch(() => {}); }, [index, images]);
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);
  const go = useCallback((d: number) => {
    const n = index + d;
    if (n < 0 || n >= images.length) { setAnim(true); setDx(0); return; }
    const w = box.current?.clientWidth || window.innerWidth;
    setAnim(true); setDx(-d * w);
    setTimeout(() => { setAnim(false); setDx(0); onIndex(n); }, 220);
  }, [index, images.length, onIndex]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [go, onClose]);

  const limit = (n: T): T => {
    const w = box.current?.clientWidth || 0, h = box.current?.clientHeight || 0;
    const mx = (w * (n.s - 1)) / 2, my = (h * (n.s - 1)) / 2;
    return { s: n.s, x: clamp(n.x, -mx, mx), y: clamp(n.y, -my, my) };
  };
  const zoomAt = (cx: number, cy: number, s: number, base: T) => {
    const r = box.current!.getBoundingClientRect();
    const px = cx - r.left - r.width / 2, py = cy - r.top - r.height / 2;
    const k = s / base.s;
    return limit({ s, x: px - (px - base.x) * k, y: py - (py - base.y) * k });
  };

  const onTouchStart = (e: React.TouchEvent) => {
    setAnim(false);
    const a = e.touches;
    if (a.length === 2) {
      g.current = { mode: "pinch", d0: Math.hypot(a[0].clientX - a[1].clientX, a[0].clientY - a[1].clientY), t0: t,
        cx: (a[0].clientX + a[1].clientX) / 2, cy: (a[0].clientY + a[1].clientY) / 2, moved: true };
    } else if (a.length === 1) {
      g.current = { mode: t.s > 1.01 ? "pan" : "swipe", x0: a[0].clientX, y0: a[0].clientY, t0: t, time: Date.now(), moved: false };
    }
  };
  const onTouchMove = (e: React.TouchEvent) => {
    const a = e.touches, s = g.current;
    if (s.mode === "pinch" && a.length === 2) {
      const d = Math.hypot(a[0].clientX - a[1].clientX, a[0].clientY - a[1].clientY);
      setT(zoomAt(s.cx, s.cy, clamp(s.t0.s * (d / s.d0), 1, 5), s.t0));
    } else if (a.length === 1 && (s.mode === "pan" || s.mode === "swipe")) {
      const mx = a[0].clientX - s.x0, my = a[0].clientY - s.y0;
      if (Math.abs(mx) > 8 || Math.abs(my) > 8) s.moved = true;
      if (s.mode === "pan") setT(limit({ s: s.t0.s, x: s.t0.x + mx, y: s.t0.y + my }));
      else setDx(mx);
    }
  };
  const lastTap = useRef({ time: 0, x: 0, y: 0 });
  const tapTimer = useRef<any>(null);
  const onTouchEnd = (e: React.TouchEvent) => {
    const s = g.current;
    if (e.touches.length > 0) { // 双指变单指：切换为拖动
      if (s.mode === "pinch") g.current = { mode: "pan", x0: e.touches[0].clientX, y0: e.touches[0].clientY, t0: t, moved: true };
      return;
    }
    if (s.mode === "pinch") { if (t.s < 1.05) { setAnim(true); setT({ x: 0, y: 0, s: 1 }); } return; }
    if (s.mode === "swipe" && s.moved) {
      const w = box.current?.clientWidth || 300;
      if (Math.abs(dx) > Math.min(80, w / 5) || (Math.abs(dx) > 30 && Date.now() - s.time < 250)) go(dx < 0 ? 1 : -1);
      else { setAnim(true); setDx(0); }
      return;
    }
    if (!s.moved) {
      const p = e.changedTouches[0], now = Date.now();
      if (now - (s.time || now) > 450) return; // 长按交给系统（保存图片菜单），不当作点击
      if (now - lastTap.current.time < 300 && Math.hypot(p.clientX - lastTap.current.x, p.clientY - lastTap.current.y) < 40) {
        clearTimeout(tapTimer.current); lastTap.current.time = 0;
        setAnim(true);
        setT(t.s > 1.01 ? { x: 0, y: 0, s: 1 } : zoomAt(p.clientX, p.clientY, 2.5, t));
      } else {
        lastTap.current = { time: now, x: p.clientX, y: p.clientY };
        tapTimer.current = setTimeout(() => setControls((c) => !c), 300);
      }
    }
  };
  // 桌面端：滚轮缩放、双击放大、单击切换控件
  const onWheel = (e: React.WheelEvent) => { setAnim(false); setT(zoomAt(e.clientX, e.clientY, clamp(t.s * (e.deltaY < 0 ? 1.15 : 1 / 1.15), 1, 5), t)); };
  const mouse = useRef<any>(null);
  const onMouseDown = (e: React.MouseEvent) => { mouse.current = { x0: e.clientX, y0: e.clientY, t0: t, moved: false }; };
  const onMouseMove = (e: React.MouseEvent) => {
    const m = mouse.current; if (!m || t.s <= 1.01) return;
    if (Math.abs(e.clientX - m.x0) + Math.abs(e.clientY - m.y0) > 4) m.moved = true;
    setT(limit({ s: m.t0.s, x: m.t0.x + e.clientX - m.x0, y: m.t0.y + e.clientY - m.y0 }));
  };
  const onMouseUp = () => { setTimeout(() => (mouse.current = null), 0); };

  if (!img) return null;
  const neighbors = [index - 1, index, index + 1];
  return (
    <Box ref={box} role="dialog" aria-label="图片查看器"
      sx={{ position: "fixed", inset: 0, zIndex: 1500, bgcolor: "#000", overflow: "hidden", touchAction: "none", userSelect: "none", WebkitUserSelect: "none" }}
      onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd} onWheel={onWheel}
      onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp}
      onClick={(e) => { if ((e as any).nativeEvent.pointerType === "touch" || "ontouchstart" in window) return; if (!mouse.current?.moved) setControls((c) => !c); }}
      onDoubleClick={(e) => { if ("ontouchstart" in window) return; setAnim(true); setT(t.s > 1.01 ? { x: 0, y: 0, s: 1 } : zoomAt(e.clientX, e.clientY, 2.5, t)); }}>
      {neighbors.map((i) => {
        const im = images[i];
        if (!im) return null;
        const off = i - index;
        const cur = off === 0;
        return (
          <Box key={im.key} sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
            transform: `translateX(calc(${off * 100}% + ${dx}px))`, transition: anim ? "transform .22s ease-out" : "none" }}>
            {!loaded[im.key] && cur && <Typography sx={{ color: "#888", position: "absolute" }}>正在加载…</Typography>}
            <img src={im.url} alt={im.name} draggable={false} onLoad={() => setLoaded((l) => ({ ...l, [im.key]: true }))}
              style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", pointerEvents: cur ? "auto" : "none",
                WebkitTouchCallout: "default", WebkitUserSelect: "none", userSelect: "none",
                transform: cur ? `translate(${t.x}px, ${t.y}px) scale(${t.s})` : undefined,
                transition: anim ? "transform .22s ease-out" : "none", cursor: t.s > 1 ? "grab" : "zoom-in" }} />
          </Box>
        );
      })}

      <Box sx={{ position: "absolute", top: 0, left: 0, right: 0, pt: "max(8px, env(safe-area-inset-top))", px: 1, pb: 3,
        display: "flex", alignItems: "center", gap: 1, color: "#fff", background: "linear-gradient(rgba(0,0,0,.65),transparent)",
        opacity: controls ? 1 : 0, pointerEvents: controls ? "auto" : "none", transition: "opacity .2s" }}
        onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <IconButton aria-label="关闭" onClick={onClose} sx={{ color: "#fff" }}><Close /></IconButton>
        <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
          <Typography sx={{ fontWeight: 700 }}>{index + 1} / {images.length}</Typography>
          <Typography variant="caption" noWrap component="div" sx={{ opacity: 0.8 }}>{img.name}</Typography>
        </Box>
        {isMobile()
          ? <Box component="button" aria-label="保存到相册" onClick={() => onDownload(img)}
              sx={{ display: "flex", alignItems: "center", gap: 0.5, color: "#fff", bgcolor: "rgba(255,255,255,.18)", border: 0, borderRadius: 99, px: 1.5, py: 0.75, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>
              <SaveAlt sx={{ fontSize: 18 }} />保存到相册
            </Box>
          : <IconButton aria-label="下载" onClick={() => onDownload(img)} sx={{ color: "#fff" }}><Download /></IconButton>}
      </Box>
      {controls && index > 0 && (
        <IconButton aria-label="上一张" onClick={(e) => { e.stopPropagation(); go(-1); }} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          sx={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#fff", bgcolor: "rgba(255,255,255,.15)", display: { xs: "none", sm: "flex" } }}><ChevronLeft fontSize="large" /></IconButton>
      )}
      {controls && index < images.length - 1 && (
        <IconButton aria-label="下一张" onClick={(e) => { e.stopPropagation(); go(1); }} onTouchStart={(e) => e.stopPropagation()} onTouchEnd={(e) => e.stopPropagation()}
          sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#fff", bgcolor: "rgba(255,255,255,.15)", display: { xs: "none", sm: "flex" } }}><ChevronRight fontSize="large" /></IconButton>
      )}
      {controls && (
        <Typography variant="caption" sx={{ position: "absolute", bottom: "max(12px, env(safe-area-inset-bottom))", left: 0, right: 0, textAlign: "center", color: "rgba(255,255,255,.7)" }}>
          {isMobile() ? "左右滑动切换 · 双指或双击放大 · 长按可保存图片" : "← → 切换 · 滚轮或双击放大 · Esc 关闭"}
        </Typography>
      )}
    </Box>
  );
}
